import {pendingStunningStrikes} from '../../../shared/rules/monkStunningStrike.mjs';
import { useCombatantName } from './CombatantNamesContext';
import { modifierDiceGroups, readModifierDice } from '../../../shared/rules/effects/rollDiceModifiers.mjs';
import { selectSaveD20 } from '../../../shared/rules/effects/saveRollEvidence.mjs';
import React, { useRef, useState } from 'react';
import CombatSavePrompt from './CombatSavePrompt';
import apiFetch from '../../../utils/apiFetch';
import { rollDiceWithBox, DICE_BOX_ROLL_SURFACE_EVENT } from '../../../utils/diceBoxManager';
import { notify } from '../../../utils/notification';
import { CUNNING_STRIKE_FAILURE_TEXT } from '../../../shared/rules/cunningStrike.mjs';

export const getPendingCunningStrikeSave = (combat, controlledIds) => {
  const ids = new Set(controlledIds.map(String));
  // Improved Cunning Strike can ask for two saves from one Sneak Attack: match each by its effect too.
  const key = resolution => `${resolution?.parentResolutionId}:${resolution?.effectId}`;
  const resolved = new Set((combat?.eventLog || []).filter(event => event.type === 'cunningStrikeResolved').map(event => key(event.resolution)));
  return (combat?.eventLog || []).find(event => event.type === 'cunningStrikePending'
    && event.turnSequence === combat.turnSequence && ids.has(String(event.resolution?.targetId))
    && !resolved.has(key(event.resolution)))?.resolution || null;
};

export default function CunningStrikeSavePrompt({ combat, campaignId, controlledIds, onCombatChange, beforeRoll, onRollResult, stunningStrike=false }) {
 const combatantName = useCombatantName();
  const pending = stunningStrike?pendingStunningStrikes(combat).find(e=>controlledIds.map(String).includes(String(e.resolution.targetId)))?.resolution:getPendingCunningStrikeSave(combat, controlledIds);
  const lock = useRef(false);
  const [resolving, setResolving] = useState(false);
  const [error, setError] = useState('');
  const answer = async (choice) => {
    if (!pending || lock.current) return;
    lock.current = true; setResolving(true); setError('');
    try {
      let d20Result; let saveDice; let modifierDice = [];
      if (choice === 'roll') {
      if (beforeRoll) await beforeRoll();
      else {
        window.dispatchEvent(new CustomEvent(DICE_BOX_ROLL_SURFACE_EVENT, { detail: { label: 'Saving Throw' } }));
        await new Promise(resolve => requestAnimationFrame(resolve));
      }
      const roll = await rollDiceWithBox([{ count: pending.rollMode && pending.rollMode !== 'normal' ? 2 : 1, sides: 20 }, ...modifierDiceGroups(pending.diceModifiers)]);
      saveDice = roll?.rolls?.[0];
      d20Result = selectSaveD20(saveDice, pending.rollMode);
      modifierDice = readModifierDice(roll, pending.diceModifiers);
      if (!Number.isInteger(d20Result) || d20Result < 1 || d20Result > 20) throw new Error('Save dice evidence was unavailable.');
      onRollResult?.(d20Result);
      }
      const response = await apiFetch(stunningStrike?`/campaigns/${encodeURIComponent(campaignId)}/monk/stunning-strike/${encodeURIComponent(pending.id)}/respond`:`/campaigns/${encodeURIComponent(campaignId)}/combat/cunning-strike/resolve`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(stunningStrike?{choice,...(choice==='roll'?{saveDice,modifierDice}:{})}:{ parentResolutionId: pending.parentResolutionId, attackerId: pending.attackerId,
          targetId: pending.targetId, effectId: pending.effectId, turnSequence: pending.turnSequence, ...(choice === 'pass' ? { choice } : { d20Result, modifierDice, saveDice }) }),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.reason || result.message || 'Save could not be submitted.');
      if (result.combat) onCombatChange(result.combat);
      const save = result.resolution?.save;
      if (save&&stunningStrike) notify(`${combatantName(pending.targetId,'Target')}: Stunning Strike · ${save.success?'Save successful: Half Speed; next attack has Advantage':result.resolution.conditionImmune?'Immune to Stunned':'Stunned'}`,save.success?'success':'danger');
      if (save&&!stunningStrike) notify(`${combatantName(pending.targetId, pending.targetName)}: Cunning Strike · ${pending.effectId}\n${save.success ? 'Save successful' : `${result.resolution.conditionImmune ? 'Immune to ' : ''}${CUNNING_STRIKE_FAILURE_TEXT[pending.effectId] || pending.effectId}`}`, save.success ? 'success' : 'danger');
    } catch (failure) { setError(failure.message); }
    finally { lock.current = false; setResolving(false); }
  };
  return <CombatSavePrompt opportunity={pending && !resolving ? {
    sourceEffect: { displayName: `${combatantName(pending.targetId, pending.targetName)}: ${stunningStrike?'Stunning Strike':`Cunning Strike · ${pending.effectId}`}` },
    targetSave: { ability: pending.saveAbility, dc: pending.dc, ...(pending.rollMode && pending.rollMode !== 'normal' ? { rollMode: pending.rollMode } : {}) },
  } : null} onRoll={() => answer('roll')} onPass={() => answer('pass')} error={error} />;
}
